/**
 * App.js — Composant racine. Définit toutes les routes et les encapsule
 * avec AuthProvider (authentification par session) et AppProvider (état du cercle actif).
 * Les routes protégées requièrent l'authentification et s'affichent dans le Layout.
 *
 * Stratégie de chargement :
 *  - Pages publiques critiques (Home, Login, Register…) : import statique
 *    → incluses dans le bundle principal, disponibles immédiatement.
 *  - Pages authentifiées : React.lazy + import dynamique
 *    → chaque page devient un chunk séparé chargé uniquement à la navigation,
 *    ce qui réduit fortement le bundle initial et améliore le LCP / TTI.
 */
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider, useApp } from './contexts/AppContext';
import Layout from './components/Layout';
import { Loader2 } from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';
import { APP_ROUTES } from './lib/routes';
import './App.css';

// ── Pages publiques (bundle principal) ────────────────────────────────────────
// Chargées immédiatement : accessibles sans authentification, visibles au
// premier rendu. Les inclure dans le bundle évite un flash de spinner.
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import InviteAccept from './pages/InviteAccept';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import Forbidden from './pages/Forbidden';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';

// ── Pages authentifiées (code splitting) ──────────────────────────────────────
// Chaque import dynamique génère un chunk JS distinct. Le code de ces pages
// n'est téléchargé que lors de la navigation effective vers la route concernée.
const UserDashboard    = lazy(() => import('./pages/UserDashboard'));
const Profile          = lazy(() => import('./pages/Profile'));
const TwoFactorSetup   = lazy(() => import('./pages/TwoFactorSetup'));
const MyCircles        = lazy(() => import('./pages/MyCircles'));
const CircleDetail     = lazy(() => import('./pages/CircleDetail'));
const PublicEvents     = lazy(() => import('./pages/PublicEvents'));
const EventDetail      = lazy(() => import('./pages/EventDetail'));
const EventPaymentSuccess = lazy(() => import('./pages/EventPaymentSuccess'));
const EventPaymentCancelled = lazy(() => import('./pages/EventPaymentCancelled'));
const AdminDashboard   = lazy(() => import('./pages/AdminDashboard'));
const AdminMembers     = lazy(() => import('./pages/AdminMembers'));
const AdminInvites     = lazy(() => import('./pages/AdminInvites'));
const AdminEvents      = lazy(() => import('./pages/AdminEvents'));
const AdminEventSignups = lazy(() => import('./pages/AdminEventSignups'));
const SiteManagerDashboard = lazy(() => import('./pages/SiteManagerDashboard'));
const SiteManagerCircles   = lazy(() => import('./pages/SiteManagerCircles'));
const SiteManagerDatabase  = lazy(() => import('./pages/SiteManagerDatabase'));
const SiteManagerDatabaseExport = lazy(() => import('./pages/SiteManagerDatabaseExport'));
const SiteManagerUserDetail = lazy(() => import('./pages/SiteManagerUserDetail'));

/** Spinner partagé — utilisé pendant la vérification d'auth et le chargement des chunks. */
function PageSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

/**
 * Garde de route qui redirige les utilisateurs non authentifiés vers /login.
 * Sauvegarde l'URL cible dans le state pour redirection après connexion.
 * Affiche un indicateur de chargement pendant la vérification de session.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Contenu à afficher lorsque l'utilisateur est authentifié.
 * @returns {React.ReactElement}
 */
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const { loading: appLoading } = useApp();
  const location = useLocation();
  if (loading || (user && appLoading)) return <PageSpinner />;
  if (!user) return <Navigate to={APP_ROUTES.login} state={{ from: location }} replace />;
  return children;
}

function RouteAccessNotice({ title, description, actionTo, actionLabel, testId }) {
  return (
    <div className="mx-auto max-w-2xl rounded-sm border border-border bg-card px-6 py-10 text-center" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Acces controle</p>
      <h1 className="mt-3 text-2xl font-semibold text-foreground">{title}</h1>
      <p className="mt-3 text-sm text-muted-foreground">{description}</p>
      <div className="mt-6">
        <Link
          to={actionTo}
          className="inline-flex items-center rounded-sm border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          {actionLabel}
        </Link>
      </div>
    </div>
  );
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const { availableViews, loading: appLoading } = useApp();
  const location = useLocation();

  if (loading || (user && appLoading)) return <PageSpinner />;
  if (!user) return <Navigate to={APP_ROUTES.login} state={{ from: location }} replace />;
  if (!availableViews.includes('admin')) return <Navigate to={APP_ROUTES.forbidden} replace />;
  return <Layout>{children}</Layout>;
}

function CircleAdminRoute({ children }) {
  const { user, loading } = useAuth();
  const { adminCircles, loading: appLoading } = useApp();
  const location = useLocation();

  if (loading || (user && appLoading)) return <PageSpinner />;
  if (!user) return <Navigate to={APP_ROUTES.login} state={{ from: location }} replace />;
  if (!adminCircles.length) return <Navigate to={APP_ROUTES.forbidden} replace />;
  return <Layout>{children}</Layout>;
}

function SiteManagerRoute({ children }) {
  const { user, loading } = useAuth();
  const { loading: appLoading } = useApp();
  const location = useLocation();
  if (loading || (user && appLoading)) return <PageSpinner />;
  if (!user) return <Navigate to={APP_ROUTES.login} state={{ from: location }} replace />;
  if (!user.is_site_manager) return <Navigate to={APP_ROUTES.forbidden} replace />;
  return <Layout>{children}</Layout>;
}

function CircleAccessRoute({ children }) {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const { circles, loading: appLoading } = useApp();
  const location = useLocation();

  if (loading || (user && appLoading)) return <PageSpinner />;
  if (!user) return <Navigate to={APP_ROUTES.login} state={{ from: location }} replace />;

  const hasCircleAccess = user.is_site_manager || circles.some(circle => String(circle.id) === String(id));
  if (!hasCircleAccess) {
    return (
      <Layout>
        <RouteAccessNotice
          title="Acces reserve"
          description="Connectez-vous a un cercle legitime avant d'ouvrir cette page."
          actionTo={APP_ROUTES.circles}
          actionLabel="Voir les cercles"
          testId="circle-access-required-page"
        />
      </Layout>
    );
  }

  return <Layout>{children}</Layout>;
}

/**
 * Raccourci : encapsule les enfants dans ProtectedRoute + Layout.
 * Permet de garder les définitions de routes concises dans AppRoutes.
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
function P({ children }) {
  return <ProtectedRoute><Layout>{children}</Layout></ProtectedRoute>;
}

function EnvironmentBanner() {
  const appEnv = process.env.REACT_APP_APP_ENV;

  if (appEnv === 'development') {
    return (
      <div
        className="border-b border-amber-300 bg-amber-100 px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.24em] text-amber-950"
        data-testid="development-banner"
      >
        ENVIRONNEMENT DE DEVELOPPEMENT
      </div>
    );
  }

  if (appEnv === 'demo') {
    return (
      <div
        className="border-b border-sky-300 bg-sky-100 px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.24em] text-sky-950"
        data-testid="demo-banner"
      >
        ENVIRONNEMENT DE DEMO
      </div>
    );
  }

  return null;
}

/**
 * Déclare toutes les routes de l'application.
 * Les routes publiques (accueil, connexion, inscription, invitation) sont accessibles sans authentification.
 * Toutes les autres routes sont encapsulées dans P (ProtectedRoute + Layout).
 *
 * Le <Suspense> englobe l'ensemble des routes : il s'active uniquement lors du
 * chargement d'un chunk lazy (navigation vers une page authentifiée pour la première fois).
 */
function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <PageSpinner />;

  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path={APP_ROUTES.login} element={user ? <Navigate to={APP_ROUTES.dashboard} replace /> : <Login />} />
        <Route path={APP_ROUTES.register} element={user ? <Navigate to={APP_ROUTES.dashboard} replace /> : <Register />} />
        <Route path={APP_ROUTES.inviteAccept()} element={<InviteAccept />} />
        <Route path={APP_ROUTES.forgotPassword} element={<ForgotPassword />} />
        <Route path={APP_ROUTES.resetPassword()} element={<ResetPassword />} />
        <Route path={APP_ROUTES.dashboard} element={<P><UserDashboard /></P>} />
        <Route path={APP_ROUTES.profile} element={<P><Profile /></P>} />
        <Route path={APP_ROUTES.twoFactorSetup} element={<P><TwoFactorSetup /></P>} />
        <Route path={APP_ROUTES.circles} element={<P><MyCircles /></P>} />
        <Route path={APP_ROUTES.circleDetail()} element={<CircleAccessRoute><CircleDetail /></CircleAccessRoute>} />
        <Route path={APP_ROUTES.events} element={<P><PublicEvents /></P>} />
        <Route path={APP_ROUTES.eventDetail()} element={user ? <Layout><EventDetail /></Layout> : <EventDetail />} />
        <Route path={APP_ROUTES.eventPaymentSuccess()} element={<P><EventPaymentSuccess /></P>} />
        <Route path={APP_ROUTES.eventPaymentCancelled()} element={<P><EventPaymentCancelled /></P>} />
        <Route path="/events/:id/payment/success" element={<P><EventPaymentSuccess /></P>} />
        <Route path="/events/:id/payment/cancelled" element={<P><EventPaymentCancelled /></P>} />
        <Route path={APP_ROUTES.admin} element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path={APP_ROUTES.adminMembers} element={<CircleAdminRoute><AdminMembers /></CircleAdminRoute>} />
        <Route path={APP_ROUTES.adminInvites} element={<CircleAdminRoute><AdminInvites /></CircleAdminRoute>} />
        <Route path={APP_ROUTES.adminEvents} element={<AdminRoute><AdminEvents /></AdminRoute>} />
        <Route path={APP_ROUTES.adminEventSignups()} element={<AdminRoute><AdminEventSignups /></AdminRoute>} />
        <Route path={APP_ROUTES.site} element={<SiteManagerRoute><SiteManagerDashboard /></SiteManagerRoute>} />
        <Route path={APP_ROUTES.siteCircles} element={<SiteManagerRoute><SiteManagerCircles /></SiteManagerRoute>} />
        <Route path={APP_ROUTES.siteDatabase} element={<SiteManagerRoute><SiteManagerDatabase /></SiteManagerRoute>} />
        <Route path={APP_ROUTES.siteDatabaseExport} element={<SiteManagerRoute><SiteManagerDatabaseExport /></SiteManagerRoute>} />
        <Route path={APP_ROUTES.siteUserDetail()} element={<SiteManagerRoute><SiteManagerUserDetail /></SiteManagerRoute>} />
        <Route path={APP_ROUTES.privacy} element={<PrivacyPolicy />} />
        <Route path={APP_ROUTES.cookies} element={<CookiePolicy />} />
        <Route path={APP_ROUTES.forbidden} element={<Forbidden />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Composant racine. Fournit le routeur, le contexte d'authentification,
 * le contexte applicatif et la couche globale de notifications toast.
 * @returns {React.ReactElement}
 */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <ErrorBoundary>
            <EnvironmentBanner />
            <AppRoutes />
            <Toaster />
            <CookieConsent />
          </ErrorBoundary>
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { CircleAdminRoute, SiteManagerRoute, AppRoutes, EnvironmentBanner };
